import React, {useState} from "react";
import {NavLink} from "react-router-dom";
import ContentBox from "../../../../../../components/content-box/ContentBox";
import PageActions from "../../../../../base/PageActions";
import Breadcrumbs from "../../../../../../components/breadcrumbs/Breadcrumbs";
import Modal from "../../../../../../components/modal/Modal";
import _image_service from "../../../../../../services/ImageService";
import '../../../../../styles/Common.scss';
import '../../WorldPages.scss';
import '../../WorldDynamicContent.scss';

interface MagicalHistoryPageProps {
}

const MagicalHistoryPage = (props: MagicalHistoryPageProps) => {

    const _map_src = `chase/world/geography/casarok_pre_cataclysm.png`;

    const page: PageActions = new PageActions({
        title: `Casarok History: Magical Age - Chase the Fox`,
        description: "The Magical Age of Casarok is the time when magical knowledge and culture greatly expanded in the world!",
        keywords: ["casarok", "casarok history", "magical age", "fantasy timeline", "fantasy world history", "fantasy classical age"],
        imgSrc: _map_src,
        url: `world/history/magical-age`
    });

    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

    return page.generateMetaContent(
        <>
            <Modal isOpen={modalIsOpen}
                   onClose={() => setModalIsOpen(false)}>
                <div className="geography-modal-view">
                    <p className="center">
                        <a href={_image_service.getCdnImageSrc(_map_src, 'full')}
                           target="_blank"
                           rel="noopener noreferrer">
                            Click here to view the full size image.
                        </a>
                    </p>
                    <img src={_image_service.getCdnImageSrc(_map_src, 'desktop')}
                         title='Casarok Pre-Cataclysm Map'
                         alt="A map of Casarok before the Great Cataclysm"/>
                </div>
            </Modal>
            <ContentBox header="The Magical Age of Casarok">
                <Breadcrumbs path={page.getUrl()}/>
                <div className="world-page-static">
                    <a id="visible-element"
                       className="switch-visible geography-modal-link"
                       onClick={() => setModalIsOpen(true)}>
                        <img className="block width-50-percent"
                             src={_image_service.getCdnImageSrc(`chase/world/geography/casarok_pre_cataclysm.png`)}
                             title='Casarok Pre-Cataclysm Map'
                             alt="A map of Casarok before the Great Cataclysm"/>
                    </a>
                </div>
                <div className="world-page-static">
                    <p className="center">
                        <em>This age covers the years of roughly 1,500 - 800 BCE.</em>
                    </p>
                    <p>
                        The Magical Age is the time in which research and knowledge rapidly expanded in Casarok,
                        particularly regarding magic.
                        The <NavLink className="world-link" to="/world/bestiary/anthro">Anthro</NavLink> race, having
                        formed some of their own nations and increasing their dominance in the world, found the pursuit
                        of magical power imperative to their continued freedom. As such, the use and understanding of
                        magic among anthros in the world exploded. Magical culture became prominent, and mage towers
                        became fashionable among magic users the world over. This knowledge of new spells and
                        enchantments even spread to
                        the <NavLink className="world-link" to="/world/bestiary/aspect">Aspect</NavLink> and
                        the <NavLink className="world-link" to="/world/bestiary/draconic">Draconic</NavLink> species
                        capable of learning a subset of those powers.
                    </p>
                    <p>
                        Outside of magic knowledge expansion, the world continued to grow further from its shaky roots.
                        Scientific research also occurred to better understand the world around them. Advancements in
                        philosophy, theater, and the arts were also made during this time and proved to help establish
                        the various cultures of the world. This cultural improvement also drew in Aspects (and to a
                        lesser degree, Draconics) to integrate further with the Anthro nations. This age ended up being
                        a time of growing peace. Toward the end of this age, mighty empires did begin to form, leading
                        into a time of greater stability in the future.
                    </p>
                    <p>
                        <em>How does Casarok's Magical Age differ from the classical era on Earth?</em><br/>
                        While the pursuit of knowledge was prominent during this time on Earth, magical research was the
                        most important. Mage towers sprouted up throughout the land as well. Mages also started to
                        become more prevalent within the world's militaries.
                    </p>
                    <h3 className="headline">Notable Events (in order of their occurrence)</h3>
                    <ul className="spaced">
                        <li>
                            Anthro nations become larger, empowered by their magical flexibility.
                        </li>
                        <li>
                            Magical research is invested in more heavily around the world to deal with any continuing
                            Aspect and Draconic threats.
                        </li>
                        <li>
                            The <NavLink className="world-link" to="/world/magic/elements">magical elements</NavLink> are
                            categorized. Their methods of casting are studied to improve their spells.
                        </li>
                        <li>
                            The first mage tower is built in what
                            is <NavLink className="world-link" to="/world/history/modern-age">modern day Erthuria</NavLink>.
                        </li>
                        <li>
                            The <NavLink className="world-link" to="/world/bestiary/draconic/dracamma">dracamma</NavLink> nation
                            that formed
                            around <NavLink className="world-link" to="/world/geography">Mt. Coreptus</NavLink> continues
                            to conquer more land for itself.
                        </li>
                        <li>
                            The elements are further organized
                            into <NavLink className="world-link" to="/world/magic/schools">magical schools</NavLink> that
                            allow for magical curricula to teach spells across elements with similar casting methods.
                        </li>
                        <li>
                            Many nations are conquered as the world begins to divide among the world's growing
                            superpowers.
                        </li>
                        <li>
                            The seeds of the first great empire of the world begin to grow in central Casarok.
                        </li>
                    </ul>
                </div>
            </ContentBox>
        </>
    );
}

export default MagicalHistoryPage;